/** QR status cart helpers (bulk scan dispatch / receive / deliver). */

export function dispatchServiceNumbersForShipment(shipment) {
  const set = new Set();
  (shipment?.service_numbers || []).forEach((n) => {
    const t = String(n || '').trim();
    if (t) set.add(t.toUpperCase());
  });
  const single = String(shipment?.service_number || '').trim();
  if (single) set.add(single.toUpperCase());
  return set;
}

/** Client-side guard when a dispatch service / ML sheet is already chosen. */
export function shipmentFitsLockedDispatchService(shipment, lockedService) {
  if (!lockedService) return { ok: true };
  const wanted = String(lockedService).trim().toUpperCase();
  if (!wanted) return { ok: true };
  const nums = dispatchServiceNumbersForShipment(shipment);
  if (nums.size === 0) {
    return {
      ok: false,
      message: `Could not verify route for this parcel. Select service ${lockedService} only for matching bookings.`,
    };
  }
  if (nums.has(wanted)) return { ok: true };
  const listed = [...nums].join(', ');
  return {
    ok: false,
    message: `This parcel belongs to service ${listed}, not ${lockedService}. It cannot join this dispatch cart.`,
  };
}

export function normalizeUnitScanRow(row) {
  const expected = Number(row?.expected_units || row?.no_of_articles || 1) || 1;
  const units = Array.isArray(row?.units) ? row.units : [];
  const scanned = new Set();
  if (Array.isArray(row?.scanned_unit_nos)) {
    row.scanned_unit_nos.forEach((n) => {
      const num = Number(n);
      if (Number.isFinite(num) && num > 0) scanned.add(num);
    });
  }
  if (row?.scanned_unit?.unit_no != null) {
    const num = Number(row.scanned_unit.unit_no);
    if (Number.isFinite(num) && num > 0) scanned.add(num);
  }
  // Single-piece shipments: a shipment-only QR counts as unit 1.
  if (expected <= 1 && scanned.size === 0 && !row?.legacy_qr) {
    scanned.add(1);
  }
  const scanned_unit_nos = [...scanned].sort((a, b) => a - b);
  return {
    ...row,
    expected_units: expected,
    units,
    scanned_unit_nos,
    unit_scan_complete: expected <= 1 || scanned_unit_nos.length >= expected || !!row.allow_legacy_bypass,
  };
}

export function mergeUnitIntoExisting(existing, incoming) {
  // beforeSet must come from existing cart only — not after merging incoming —
  // or a new unit is treated as already scanned (false "already scanned" toast).
  const existingNormalized = normalizeUnitScanRow(existing);
  const beforeSet = new Set(
    (existingNormalized.scanned_unit_nos || [])
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n > 0),
  );
  const base = normalizeUnitScanRow({
    ...existing,
    ...incoming,
    scanned_unit_nos: existing.scanned_unit_nos || [],
    units: incoming.units?.length ? incoming.units : existing.units,
    expected_units: incoming.expected_units || existing.expected_units,
    legacy_qr: incoming.legacy_qr || existing.legacy_qr,
    allow_legacy_bypass: existing.allow_legacy_bypass || incoming.allow_legacy_bypass,
  });
  const scanned = new Set(beforeSet);
  const attemptedUnit = incoming?.scanned_unit?.unit_no != null
    ? Number(incoming.scanned_unit.unit_no)
    : null;
  if (attemptedUnit != null && Number.isFinite(attemptedUnit) && attemptedUnit > 0) {
    scanned.add(attemptedUnit);
  }
  if (Array.isArray(incoming?.scanned_unit_nos)) {
    incoming.scanned_unit_nos.forEach((n) => {
      const num = Number(n);
      if (Number.isFinite(num) && num > 0) scanned.add(num);
    });
  }
  const scanned_unit_nos = [...scanned].filter((n) => Number.isFinite(n) && n > 0).sort((a, b) => a - b);
  const newlyAdded = scanned_unit_nos.filter((n) => !beforeSet.has(n));
  const expected = Number(base.expected_units || 1) || 1;
  const addedUnitNo = newlyAdded.length === 1
    ? newlyAdded[0]
    : (newlyAdded.length > 1 ? newlyAdded[newlyAdded.length - 1] : null);
  const duplicateUnitNo = newlyAdded.length === 0
    && attemptedUnit != null
    && beforeSet.has(attemptedUnit)
    ? attemptedUnit
    : null;
  return {
    ...base,
    scanned_unit: incoming.scanned_unit || existing.scanned_unit,
    scanned_unit_nos,
    unit_scan_complete: expected <= 1 || scanned_unit_nos.length >= expected || !!base.allow_legacy_bypass,
    _unit_added: newlyAdded.length > 0,
    _unit_duplicate: duplicateUnitNo != null,
    _added_unit_no: addedUnitNo,
    _duplicate_unit_no: duplicateUnitNo,
  };
}

export function mergeCartShipments(existing, incoming) {
  const map = new Map(
    (existing || [])
      .filter((s) => s?.shipment_id)
      .map((s) => [s.shipment_id, normalizeUnitScanRow(s)]),
  );
  (incoming || []).forEach((row) => {
    if (!row?.shipment_id) return;
    const prev = map.get(row.shipment_id);
    if (prev) {
      map.set(row.shipment_id, mergeUnitIntoExisting(prev, row));
    } else {
      map.set(row.shipment_id, normalizeUnitScanRow(row));
    }
  });
  return Array.from(map.values());
}

export function mergeCartFailed(existing, incoming) {
  const map = new Map((existing || []).map((f) => [f.shipment_id, f]));
  (incoming || []).forEach((f) => {
    if (f?.shipment_id) map.set(f.shipment_id, f);
  });
  return Array.from(map.values());
}

/** Attach a live-scanned unit number onto resolve rows when the API omitted it. */
export function enrichShipmentsWithScannedUnit(shipments, unitNo) {
  if (unitNo == null || !Array.isArray(shipments)) return shipments || [];
  return shipments.map((row) => {
    if (row?.notice_only) return row;
    const existingNos = Array.isArray(row.scanned_unit_nos) ? row.scanned_unit_nos : [];
    const nos = [...new Set([...existingNos, Number(unitNo)].filter((n) => Number.isFinite(n) && n > 0))];
    const match = (row.units || []).find((u) => Number(u.unit_no) === Number(unitNo));
    return {
      ...row,
      scanned_unit: row.scanned_unit?.unit_no != null
        ? row.scanned_unit
        : {
            unit_no: Number(unitNo),
            article_type: match?.article_type || row.article_type || 'PACKAGE',
          },
      scanned_unit_nos: nos.length ? nos : [Number(unitNo)],
      legacy_qr: false,
    };
  });
}
